/**
 * EPBCS Phase 2 — Revenue & OpEx sections (sub-view driven by sidebar route).
 */
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash } from "@phosphor-icons/react";
import api from "@/lib/api";
import BudgetGrid from "./BudgetGrid";
import ContractForm from "./ContractForm";
import HospitalityGrid from "./HospitalityGrid";
import { useDims } from "./povUtils";
import { Field, Input, Select, Card, Btn } from "./common";

export function RevenueSection({ sub }) {
  const tab = ["other", "driver", "contract", "product", "hosp1", "hosp2"].includes(sub) ? sub : "other";
  return (
    <div data-testid="epbcs-revenue-section">
      {tab === "other" && <BudgetGrid form="revenue_other" />}
      {tab === "driver" && <BudgetGrid form="revenue_driver" />}
      {tab === "contract" && <ContractForm form="revenue_contract" />}
      {tab === "product" && <BudgetGrid form="revenue_product" />}
      {tab === "hosp1" && <HospitalityGrid form="revenue_hosp_form1" />}
      {tab === "hosp2" && <HospitalityGrid form="revenue_hosp_form2" />}
    </div>
  );
}

export function OpexSection({ sub }) {
  const tab = ["contract", "driver", "other", "hosp1", "hosp2", "material", "bom", "transport", "fuel", "fleet"].includes(sub)
    ? sub : "contract";
  return (
    <div data-testid="epbcs-opex-section">
      {tab === "contract" && <ContractForm form="opex_contract" />}
      {tab === "driver" && <BudgetGrid form="opex_driver" />}
      {tab === "other" && <BudgetGrid form="opex_other" />}
      {tab === "hosp1" && <HospitalityGrid form="opex_hosp_form1" />}
      {tab === "hosp2" && <HospitalityGrid form="opex_hosp_form2" />}
      {tab === "material" && <BudgetGrid form="opex_material" />}
      {tab === "bom" && <BomMasterTab />}
      {tab === "transport" && <TransportSetupTab />}
      {tab === "fuel" && <BudgetGrid form="opex_fuel" />}
      {tab === "fleet" && <BudgetGrid form="opex_fleet" />}
    </div>
  );
}

const BomMasterTab = () => {
  const dims = useDims();
  const [entity, setEntity] = useState("");
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [draft, setDraft] = useState(null);
  useEffect(() => { if (dims && !entity) setEntity(dims.entity?.[0]?.code || ""); }, [dims]); // eslint-disable-line
  const load = () => {
    if (!entity) return;
    api.post("/epbcs/bom-master/list", { entity })
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || e.message));
  };
  useEffect(load, [entity]); // eslint-disable-line
  if (!dims) return <p className="text-sm text-slate-400">Loading masters…</p>;
  if (error) return <p className="text-sm text-red-600" data-testid="bom-error">{error}</p>;
  const canEdit = data && (data.perm === "input" || data.perm === "full");
  const fgs = data?.finished_goods || [];
  const rms = data?.raw_materials || [];

  const save = async () => {
    try {
      await api.post("/epbcs/bom-master", { entity, finished_good: draft.finished_good,
        raw_material: draft.raw_material, qty: +draft.qty || 0 });
      toast.success("BOM row saved");
      setDraft(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save BOM row");
    }
  };
  const del = async (id) => {
    await api.delete(`/epbcs/bom-master/${id}`);
    toast.success("BOM row removed");
    load();
  };

  const rowsByFg = {};
  (data?.rows || []).forEach((r) => { (rowsByFg[r.finished_good] = rowsByFg[r.finished_good] || []).push(r); });

  return (
    <div className="space-y-4" data-testid="bom-master-tab">
      <div className="flex items-center gap-2">
        <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Entity</label>
        <Select value={entity} data-testid="bom-entity-select" className="w-48"
          onChange={(e) => setEntity(e.target.value)}>
          {(dims.entity || []).map((e) => <option key={e.code} value={e.code}>{e.code} — {e.name}</option>)}
        </Select>
        {canEdit && !draft && fgs.length > 0 && rms.length > 0 && (
          <button onClick={() => setDraft({ finished_good: fgs[0].code, raw_material: rms[0].code, qty: "" })}
            data-testid="bom-add-btn"
            className="ml-auto inline-flex items-center gap-1.5 bg-[#0B1E3F] text-white px-3 py-2 text-[11px] font-bold uppercase tracking-wider rounded">
            <Plus size={13} weight="bold" /> Add BOM
          </button>
        )}
      </div>
      <p className="text-[11px] text-slate-500">
        Quantity of each Raw Material required to produce 1 unit of a Finished Good.
        Feeds "Qty Required" on the Material Cost Budgeting form (Qty Required = Production Qty × BOM Quantity).
      </p>
      {data && (fgs.length === 0 || rms.length === 0) && (
        <p className="text-sm text-amber-700 rounded-sm border border-amber-200 bg-amber-50 p-4" data-testid="bom-no-drivers">
          No Finished Good / Raw Material members found — add Products tagged Type = "FG" / "RM" under
          Setup &gt; Dimension Masters &gt; Products first.
        </p>
      )}
      {draft && (
        <div className="rounded-sm border border-[#0B1E3F]/30 bg-slate-50 p-4 flex flex-wrap items-end gap-3" data-testid="bom-draft">
          <Field label="Finished Good *">
            <Select value={draft.finished_good} data-testid="bom-draft-fg"
              onChange={(e) => setDraft({ ...draft, finished_good: e.target.value })}>
              {fgs.map((fg) => <option key={fg.code} value={fg.code}>{fg.code} — {fg.name}</option>)}
            </Select>
          </Field>
          <Field label="Raw Material *">
            <Select value={draft.raw_material} data-testid="bom-draft-rm"
              onChange={(e) => setDraft({ ...draft, raw_material: e.target.value })}>
              {rms.map((rm) => <option key={rm.code} value={rm.code}>{rm.code} — {rm.name}</option>)}
            </Select>
          </Field>
          <Field label="Quantity *">
            <Input type="number" min="0" step="0.01" value={draft.qty} data-testid="bom-draft-qty"
              onChange={(e) => setDraft({ ...draft, qty: e.target.value })} className="w-28" />
          </Field>
          <div className="flex gap-2">
            <button onClick={() => setDraft(null)} className="border border-slate-300 text-slate-600 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider rounded">Cancel</button>
            <Btn onClick={save} disabled={!draft.finished_good || !draft.raw_material || !(+draft.qty > 0)} data-testid="bom-draft-save">Save</Btn>
          </div>
        </div>
      )}
      {!data ? <p className="text-sm text-slate-400">Loading…</p> : fgs.length > 0 && (
        <div className="rounded-sm border border-slate-200 bg-white shadow-sm divide-y divide-slate-100">
          {fgs.map((fg) => {
            const rows = rowsByFg[fg.code] || [];
            return (
              <div key={fg.code} className="p-3" data-testid={`bom-fg-block-${fg.code}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[12px] font-bold">{fg.code} — {fg.name}</span>
                  {canEdit && (
                    <button onClick={() => setDraft({ finished_good: fg.code, raw_material: rms[0]?.code || "", qty: "" })}
                      data-testid={`bom-add-rm-${fg.code}`}
                      className="text-[10px] font-bold uppercase tracking-wider text-[#0B1E3F] hover:text-[#C8A24B]">
                      <Plus size={11} className="inline mr-1" />Add RM
                    </button>
                  )}
                </div>
                {rows.length === 0 ? (
                  <p className="text-[11px] text-slate-400">No Raw Materials linked yet.</p>
                ) : (
                  <table className="text-[12px] w-full">
                    <thead>
                      <tr className="text-left text-[9px] tracking-[0.12em] uppercase text-slate-500 border-b border-slate-200">
                        <th className="py-1.5 pr-3">Raw Material</th>
                        <th className="py-1.5 pr-3 text-right">Quantity</th>
                        {canEdit && <th className="w-8" />}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r) => {
                        const rm = rms.find((x) => x.code === r.raw_material);
                        return (
                          <tr key={r.id} className="border-b border-slate-50" data-testid={`bom-row-${fg.code}-${r.raw_material}`}>
                            <td className="py-1.5 pr-3">{r.raw_material} — {rm?.name || ""}</td>
                            <td className="py-1.5 pr-3 text-right font-mono">{r.qty}</td>
                            {canEdit && (
                              <td className="py-1.5 text-right">
                                <button onClick={() => del(r.id)} data-testid={`bom-delete-${fg.code}-${r.raw_material}`}
                                  className="text-slate-400 hover:text-red-600"><Trash size={13} /></button>
                              </td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const TransportSetupTab = () => {
  const [data, setData] = useState(null);
  const [vehData, setVehData] = useState(null);
  const [error, setError] = useState(null);
  const [expDraft, setExpDraft] = useState(null);
  const [vehDraft, setVehDraft] = useState(null);

  const load = () => {
    api.post("/epbcs/transport-expenses/list", {}).then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || e.message));
    api.post("/epbcs/vehicles/list", {}).then((r) => setVehData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || e.message));
  };
  useEffect(load, []); // eslint-disable-line
  if (error) return <p className="text-sm text-red-600" data-testid="transport-error">{error}</p>;
  if (!data || !vehData) return <p className="text-sm text-slate-400">Loading…</p>;
  const canEdit = data.perm === "input" || data.perm === "full";

  const saveExpense = async () => {
    try {
      await api.post("/epbcs/transport-expenses", expDraft);
      toast.success("Expense saved");
      setExpDraft(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save expense"); }
  };
  const deleteExpense = async (id) => { await api.delete(`/epbcs/transport-expenses/${id}`); load(); };

  const saveVehicle = async () => {
    try {
      await api.post("/epbcs/vehicles", vehDraft);
      toast.success("Vehicle saved");
      setVehDraft(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save vehicle"); }
  };
  const deleteVehicle = async (id) => { await api.delete(`/epbcs/vehicles/${id}`); load(); };

  return (
    <div className="space-y-5" data-testid="transport-setup-tab">
      <Card title="Transportation Expenses"
        subtitle='Expenses relating to transportation — set a Fuel Type (e.g. "diesel") on Diesel/Petrol so they drive the Fuel Cost form; leave it blank for fleet expenses (Inspection, Renewal, Maintenance, etc.).'
        actions={canEdit && !expDraft && (
          <button onClick={() => setExpDraft({ expense_name: "", account_code: "", fuel_type: "" })}
            data-testid="new-transport-expense-btn"
            className="inline-flex items-center gap-1.5 bg-[#0B1E3F] text-white px-3 py-2 text-[11px] font-bold uppercase tracking-wider rounded">
            <Plus size={13} weight="bold" /> Add expense
          </button>
        )} testid="transport-expenses-card">
        {expDraft && (
          <div className="rounded-sm border border-[#0B1E3F]/30 bg-slate-50 p-4 mb-3 grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="transport-expense-draft">
            <Field label="Expense Name *"><Input value={expDraft.expense_name} data-testid="expense-name-input"
              onChange={(e) => setExpDraft({ ...expDraft, expense_name: e.target.value })} /></Field>
            <Field label="Account Mapping *">
              <Select value={expDraft.account_code} data-testid="expense-account-select"
                onChange={(e) => setExpDraft({ ...expDraft, account_code: e.target.value })}>
                <option value="">Select…</option>
                {(data.accounts || []).map((a) => <option key={a.code} value={a.code}>{a.code} — {a.name}</option>)}
              </Select>
            </Field>
            <Field label="Fuel Type (Diesel/Petrol only)">
              <Input placeholder="diesel / petrol — blank for fleet" value={expDraft.fuel_type} data-testid="expense-fuel-input"
                onChange={(e) => setExpDraft({ ...expDraft, fuel_type: e.target.value })} />
            </Field>
            <div className="flex items-end gap-2">
              <button onClick={() => setExpDraft(null)} className="border border-slate-300 text-slate-600 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider rounded">Cancel</button>
              <Btn onClick={saveExpense} disabled={!expDraft.expense_name || !expDraft.account_code} data-testid="expense-save-btn">Save</Btn>
            </div>
          </div>
        )}
        <table className="text-[12px] w-full">
          <thead>
            <tr className="text-left text-[9px] tracking-[0.12em] uppercase text-slate-500 border-b border-slate-200">
              <th className="py-2 pr-3">Expense Name</th><th className="py-2 pr-3">Account Mapping</th>
              <th className="py-2 pr-3">Fuel Type</th>{canEdit && <th className="w-8" />}
            </tr>
          </thead>
          <tbody>
            {(data.expenses || []).map((e) => (
              <tr key={e.id} className="border-b border-slate-50" data-testid={`transport-expense-row-${e.expense_name}`}>
                <td className="py-1.5 pr-3 font-medium">{e.expense_name}</td>
                <td className="py-1.5 pr-3 font-mono">{e.account_code}</td>
                <td className="py-1.5 pr-3 text-slate-500">{e.fuel_type || "—"}</td>
                {canEdit && <td className="py-1.5"><button onClick={() => deleteExpense(e.id)} className="text-slate-400 hover:text-red-600" data-testid={`transport-expense-delete-${e.expense_name}`}><Trash size={13} /></button></td>}
              </tr>
            ))}
            {(data.expenses || []).length === 0 && <tr><td colSpan={4} className="py-4 text-center text-slate-400">No expenses yet.</td></tr>}
          </tbody>
        </table>
      </Card>

      <Card title="Vehicles" subtitle="Vehicles used for fuel and fleet expense budgeting."
        actions={canEdit && !vehDraft && (
          <button onClick={() => setVehDraft({ name: "", fuel_type: vehData.fuel_types?.[0] || "" })}
            data-testid="new-vehicle-btn"
            className="inline-flex items-center gap-1.5 bg-[#0B1E3F] text-white px-3 py-2 text-[11px] font-bold uppercase tracking-wider rounded">
            <Plus size={13} weight="bold" /> Add vehicle
          </button>
        )} testid="vehicles-card">
        {vehDraft && (
          <div className="rounded-sm border border-[#0B1E3F]/30 bg-slate-50 p-4 mb-3 grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="vehicle-draft">
            <Field label="Name *"><Input value={vehDraft.name} data-testid="vehicle-name-input"
              onChange={(e) => setVehDraft({ ...vehDraft, name: e.target.value })} /></Field>
            <Field label="Type *">
              <Select value={vehDraft.fuel_type} data-testid="vehicle-type-select"
                onChange={(e) => setVehDraft({ ...vehDraft, fuel_type: e.target.value })}>
                <option value="">Select…</option>
                {(vehData.fuel_types || []).map((f) => <option key={f} value={f}>{f}</option>)}
              </Select>
            </Field>
            <div className="flex items-end gap-2">
              <button onClick={() => setVehDraft(null)} className="border border-slate-300 text-slate-600 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider rounded">Cancel</button>
              <Btn onClick={saveVehicle} disabled={!vehDraft.name || !vehDraft.fuel_type} data-testid="vehicle-save-btn">Save</Btn>
            </div>
          </div>
        )}
        {(vehData.fuel_types || []).length === 0 && (
          <p className="text-[12px] text-amber-700 bg-amber-50 border border-amber-200 rounded-sm p-3 mb-3" data-testid="vehicle-no-fuel-types">
            Add a fuel-type expense above (e.g. "Diesel Expense" with Fuel Type = diesel) before creating vehicles.
          </p>
        )}
        <table className="text-[12px] w-full">
          <thead>
            <tr className="text-left text-[9px] tracking-[0.12em] uppercase text-slate-500 border-b border-slate-200">
              <th className="py-2 pr-3">Code</th><th className="py-2 pr-3">Name</th><th className="py-2 pr-3">Type</th>
              {canEdit && <th className="w-8" />}
            </tr>
          </thead>
          <tbody>
            {(vehData.vehicles || []).map((v) => (
              <tr key={v.id} className="border-b border-slate-50" data-testid={`vehicle-row-${v.code}`}>
                <td className="py-1.5 pr-3 font-mono">{v.code}</td>
                <td className="py-1.5 pr-3 font-medium">{v.name}</td>
                <td className="py-1.5 pr-3 text-slate-500">{v.fuel_type}</td>
                {canEdit && <td className="py-1.5"><button onClick={() => deleteVehicle(v.id)} className="text-slate-400 hover:text-red-600" data-testid={`vehicle-delete-${v.code}`}><Trash size={13} /></button></td>}
              </tr>
            ))}
            {(vehData.vehicles || []).length === 0 && <tr><td colSpan={4} className="py-4 text-center text-slate-400">No vehicles yet.</td></tr>}
          </tbody>
        </table>
      </Card>
    </div>
  );
};
